/**
 * In-App Notifications API Service - APB-1296
 *
 * Epic: APB-1306 - [Notification] EPIC-2 - Approval Workflow Notifications (P1)
 *
 * Service for interacting with the in-app notifications REST API.
 * Provides methods for fetching, marking as read, and managing notifications.
 */

import { createApiClient } from './apiClient';
import { parseAxiosError, AppError, ErrorCode } from '../../utils/errorHandler';

// Create API client instance
const apiClient = createApiClient();

// =============================================================================
// TYPES
// =============================================================================

export interface NotificationAction {
  label: string;
  action: string;
  url?: string;
  params?: Record<string, unknown>;
}

export interface Notification {
  id: string;
  tenant_id: string;
  user_id: string;
  notification_type: string;
  priority: string;
  title: string;
  message: string;
  is_read: boolean;
  read_at: string | null;
  alert_id: string | null;
  action_id: string | null;
  reference_id: string | null;
  reference_type: string | null;
  action_payload: NotificationAction[];
  metadata: Record<string, unknown>;
  scheduled_at: string | null;
  expires_at: string | null;
  source_service: string | null;
  created_at: string;
  updated_at: string;
}

export interface NotificationListResponse {
  success: boolean;
  notifications: Notification[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
  unread_count: number;
}

export interface UnreadCountResponse {
  success: boolean;
  unread_count: number;
  by_type: Record<string, number>;
  by_priority: Record<string, number>;
}

export interface MarkReadResponse {
  success: boolean;
  notification_id: string;
  is_read: boolean;
  read_at: string | null;
}

export interface MarkAllReadResponse {
  success: boolean;
  count: number;
  message: string;
}

export interface NotificationTypeInfo {
  value: string;
  label: string;
  description: string;
}

export interface NotificationTypesResponse {
  success: boolean;
  types: NotificationTypeInfo[];
}

export interface NotificationPriorityInfo {
  value: string;
  label: string;
  description: string;
}

export interface NotificationPrioritiesResponse {
  success: boolean;
  priorities: NotificationPriorityInfo[];
}

export interface ListNotificationsParams {
  notification_type?: string;
  priority?: string;
  is_read?: boolean;
  source_service?: string;
  alert_id?: string;
  action_id?: string;
  page?: number;
  page_size?: number;
}

// =============================================================================
// NOTIFICATION TYPES ENUM (mirrors backend)
// =============================================================================

export const NotificationType = {
  APPROVAL_REQUIRED: 'approval_required',
  ACTION_APPROVED: 'action_approved',
  ACTION_REJECTED: 'action_rejected',
  ACTION_EXPIRED: 'action_expired',
  ESCALATION: 'escalation',
  ALERT_CRITICAL: 'alert_critical',
  ALERT_UPDATE: 'alert_update',
  ALERT_ASSIGNED: 'alert_assigned',
  ANOMALY_DETECTED: 'anomaly_detected',
  SYSTEM: 'system',
  INFO: 'info',
} as const;

export type NotificationTypeValue = typeof NotificationType[keyof typeof NotificationType];

// =============================================================================
// NOTIFICATION PRIORITIES ENUM (mirrors backend)
// =============================================================================

export const NotificationPriority = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  INFO: 'info',
} as const;

export type NotificationPriorityValue = typeof NotificationPriority[keyof typeof NotificationPriority];

// =============================================================================
// API SERVICE
// =============================================================================

/**
 * Notifications API Service
 *
 * Provides methods to interact with the in-app notifications backend API.
 */
export const notificationsApi = {
  /**
   * List notifications for the current user
   */
  async listNotifications(params: ListNotificationsParams = {}): Promise<NotificationListResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params.notification_type) queryParams.append('notification_type', params.notification_type);
      if (params.priority) queryParams.append('priority', params.priority);
      if (params.is_read !== undefined) queryParams.append('is_read', String(params.is_read));
      if (params.source_service) queryParams.append('source_service', params.source_service);
      if (params.alert_id) queryParams.append('alert_id', params.alert_id);
      if (params.action_id) queryParams.append('action_id', params.action_id);
      if (params.page) queryParams.append('page', String(params.page));
      if (params.page_size) queryParams.append('page_size', String(params.page_size));

      const queryString = queryParams.toString();
      const url = `/api/notifications${queryString ? `?${queryString}` : ''}`;

      const response = await apiClient.get<NotificationListResponse>(url);
      return response.data;
    } catch (error) {
      const appError = parseAxiosError(error);
      console.error('Failed to fetch notifications:', appError);
      throw appError;
    }
  },

  /**
   * Get unread notification count with breakdown
   */
  async getUnreadCount(): Promise<UnreadCountResponse> {
    try {
      const response = await apiClient.get<UnreadCountResponse>('/api/notifications/unread-count');
      return response.data;
    } catch (error) {
      const appError = parseAxiosError(error);
      console.error('Failed to fetch unread count:', appError);
      // Return zero count on error to prevent UI issues
      return {
        success: false,
        unread_count: 0,
        by_type: {},
        by_priority: {},
      };
    }
  },

  /**
   * Get a single notification by ID
   */
  async getNotification(notificationId: string): Promise<{ success: boolean; notification: Notification }> {
    try {
      const response = await apiClient.get<{ success: boolean; notification: Notification }>(
        `/api/notifications/${notificationId}`
      );
      return response.data;
    } catch (error) {
      const appError = parseAxiosError(error);
      console.error('Failed to fetch notification:', appError);
      throw appError;
    }
  },

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: string): Promise<MarkReadResponse> {
    try {
      const response = await apiClient.put<MarkReadResponse>(
        `/api/notifications/${notificationId}/read`
      );
      return response.data;
    } catch (error) {
      const appError = parseAxiosError(error);
      console.error('Failed to mark notification as read:', appError);
      throw appError;
    }
  },

  /**
   * Mark a notification as unread
   */
  async markAsUnread(notificationId: string): Promise<MarkReadResponse> {
    try {
      const response = await apiClient.put<MarkReadResponse>(
        `/api/notifications/${notificationId}/unread`
      );
      return response.data;
    } catch (error) {
      const appError = parseAxiosError(error);
      console.error('Failed to mark notification as unread:', appError);
      throw appError;
    }
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(notificationType?: string): Promise<MarkAllReadResponse> {
    try {
      const queryParams = notificationType ? `?notification_type=${notificationType}` : '';
      const response = await apiClient.put<MarkAllReadResponse>(
        `/api/notifications/read-all${queryParams}`
      );
      return response.data;
    } catch (error) {
      const appError = parseAxiosError(error);
      console.error('Failed to mark all notifications as read:', appError);
      throw appError;
    }
  },

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId: string): Promise<{ success: boolean; deleted: boolean }> {
    try {
      const response = await apiClient.delete<{ success: boolean; notification_id: string; deleted: boolean }>(
        `/api/notifications/${notificationId}`
      );
      return { success: response.data.success, deleted: response.data.deleted };
    } catch (error) {
      const appError = parseAxiosError(error);
      console.error('Failed to delete notification:', appError);
      throw appError;
    }
  },

  /**
   * Get available notification types
   */
  async getNotificationTypes(): Promise<NotificationTypesResponse> {
    try {
      const response = await apiClient.get<NotificationTypesResponse>('/api/notifications/types');
      return response.data;
    } catch (error) {
      // Return fallback types on error
      return {
        success: true,
        types: [
          { value: 'approval_required', label: 'Approval Required', description: 'Action requires your approval' },
          { value: 'action_approved', label: 'Action Approved', description: 'An action was approved' },
          { value: 'action_rejected', label: 'Action Rejected', description: 'An action was rejected' },
          { value: 'alert_critical', label: 'Critical Alert', description: 'Critical security alert detected' },
          { value: 'system', label: 'System', description: 'System notification' },
        ],
      };
    }
  },

  /**
   * Get available notification priorities
   */
  async getNotificationPriorities(): Promise<NotificationPrioritiesResponse> {
    try {
      const response = await apiClient.get<NotificationPrioritiesResponse>('/api/notifications/priorities');
      return response.data;
    } catch (error) {
      // Return fallback priorities on error
      return {
        success: true,
        priorities: [
          { value: 'critical', label: 'Critical', description: 'Immediate attention required (red badge)' },
          { value: 'high', label: 'High', description: 'Important notification (orange badge)' },
          { value: 'medium', label: 'Medium', description: 'Normal priority (blue badge)' },
          { value: 'low', label: 'Low', description: 'Low priority (gray badge)' },
          { value: 'info', label: 'Info', description: 'Informational (no badge)' },
        ],
      };
    }
  },
};

export default notificationsApi;
